import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';
import { serviceKind, presentService, formatPrice } from '@/utils/servicePresentation';
import { sortServicesCustom } from '@/utils/sortServices';

export const ServicesSection = ({ services, loading, error, onRetry, onQuote, onDetails }) => {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const sorted = useMemo(() => {
    const all = sortServicesCustom(services).filter((s) => !s.name.toLowerCase().includes('andet'));
    const order = ['wheel', 'service', 'diagnostics'];
    return [...all].sort((a, b) => {
      const rank = (s) => order.includes(serviceKind(s)) ? order.indexOf(serviceKind(s)) : 3;
      return rank(a) - rank(b);
    });
  }, [services]);
  const trimmedQuery = query.trim();
  const matches = sorted.filter((s) => `${s.name} ${s.description}`.toLowerCase().includes(trimmedQuery.toLowerCase()));
  const visible = trimmedQuery ? matches : (expanded ? sorted : sorted.slice(0, 3));
  return (
    <section id="services" className="e-section" data-testid="services-section">
      <div className="e-container">
        <div className="e-section-head">
          <p className="e-eyebrow">Vores tjenester</p><h2>Hvad har din bil brug for?</h2>
          <p>En enkelt opgave eller et grundigt eftersyn. Vi kommer til dig.</p>
        </div>
        <div className="e-service-search">
          <label htmlFor="service-search" className="sr-only">Søg efter ydelse</label>
          <input id="service-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Søg efter en ydelse" data-testid="services-search-input" />
          {query && <button type="button" aria-label="Ryd søgning" onClick={() => setQuery('')} data-testid="clear-search-btn"><X size={20} /></button>}
        </div>
        {error ? <div role="alert" data-testid="services-load-error"><p>Vi kunne ikke hente ydelserne.</p><button className="e-text-link" onClick={onRetry} data-testid="services-retry-btn">Prøv igen</button></div> :
          <div className="e-service-grid">
            {loading ? [1,2,3].map((n) => <div key={n} className="e-service-card h-96 animate-pulse" data-testid={`service-skeleton-${n}`} />) : visible.map((service) => {
              const item = presentService(service);
              return <article key={service.id} className="e-service-card" data-testid={`service-card-${service.id}`}>
                <img className="e-service-image" src={service.image_url} alt={item.title} loading="lazy" decoding="async" data-testid={`service-image-${service.id}`} />
                <div className="e-service-content">
                  <h3 data-testid={`service-title-${service.id}`}>{item.title}</h3><p>{item.description}</p>
                  <div className="e-service-price"><strong data-testid={`service-price-${service.id}`}>{service.quote_required && service.price > 0 ? 'Fra ' : ''}{service.price > 0 ? formatPrice(service.price) : 'Efter tilbud'}</strong><span>{service.duration_minutes} min</span></div>
                  {service.quote_required ? <button type="button" className="e-button" onClick={() => onQuote(service)} data-testid={`service-quote-${service.id}`}>Få tilbud på {item.title.toLocaleLowerCase('da-DK')} <ArrowRight size={18} className="shrink-0" /></button> :
                    <Link className="e-button" to={`/book?service=${service.id}`} data-testid={`service-book-${service.id}`}>{item.action} <ArrowRight size={18} className="shrink-0" /></Link>}
                  <button type="button" className="e-text-link mt-2 self-start" onClick={() => onDetails(service)} data-testid={`service-details-${service.id}`}>Læs om ydelsen</button>
                </div>
              </article>;
            })}
          </div>}
        {!loading && !error && trimmedQuery && !visible.length && <p data-testid="services-empty-state">Ingen ydelser matcher din søgning.</p>}
        <div className="e-service-tools">
          {!loading && !trimmedQuery && sorted.length > 3 && <button type="button" className="e-button secondary" onClick={() => setExpanded(!expanded)} data-testid="show-more-services-btn">{expanded ? 'Vis færre ydelser' : 'Se alle ydelser'}</button>}
          <button type="button" className="e-text-link" onClick={() => onQuote(null)} data-testid="services-custom-quote-btn">I tvivl om opgaven? Få et tilbud <ArrowRight size={16} /></button>
        </div>
      </div>
    </section>
  );
};